import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@material-ui/core';
import * as React from 'react';
import { ReservationService } from 'services';
import { Reservation } from 'interfaces';

interface Props {
  open: boolean;
  onClose: () => void;
  /** YYYY-MM-DD - the date to query */
  date: string;
  /** ISO-ish "YYYY-MM-DDTHH:MM" - the time window start the admin is considering */
  reservedAt: string;
  /** Minutes - how long this prospective reservation would hold the table(s) */
  durationMinutes: number;
}

/**
 * Shows every approved/completed reservation on the chosen date with its
 * times and tables, so the admin can see what is already booked before
 * assigning tables. Reservations whose time window overlaps with the
 * admin's prospective slot are highlighted in red so the conflict is
 * obvious at a glance.
 */
export const TableAvailabilityDialog: React.FC<Props> = ({
  open,
  onClose,
  date,
  reservedAt,
  durationMinutes,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<Reservation[]>([]);

  const prospectStartMs = React.useMemo(() => {
    if (!reservedAt) return null;
    const t = new Date(reservedAt.replace('T', ' '));
    return isNaN(t.getTime()) ? null : t.getTime();
  }, [reservedAt]);

  const prospectEndMs = React.useMemo(() => {
    if (prospectStartMs === null) return null;
    return prospectStartMs + (durationMinutes || 120) * 60 * 1000;
  }, [prospectStartMs, durationMinutes]);

  React.useEffect(() => {
    if (!open || !date) return;
    setLoading(true);
    ReservationService.getAll({ date })
      .then((data) => {
        const visible = (data || []).filter(
          (r) => r.status === 'approved' || r.status === 'completed',
        );
        visible.sort((a, b) => a.reservedAt.localeCompare(b.reservedAt));
        setRows(visible);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open, date]);

  const fmtTime = (s: string) => {
    const d = new Date(s);
    return isNaN(d.getTime())
      ? s
      : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const endOf = (r: Reservation) => {
    const start = new Date(r.reservedAt).getTime();
    return start + (r.durationMinutes || 120) * 60 * 1000;
  };

  const overlapsProspect = (r: Reservation): boolean => {
    if (prospectStartMs === null || prospectEndMs === null) return false;
    const start = new Date(r.reservedAt).getTime();
    const end = endOf(r);
    return start < prospectEndMs && end > prospectStartMs;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Bookings on {date || '—'}
        {prospectStartMs !== null && prospectEndMs !== null && (
          <Typography
            variant="caption"
            display="block"
            color="textSecondary"
          >
            Comparing against your proposed slot:{' '}
            {fmtTime(reservedAt)} +{' '}
            {durationMinutes || 120} min (=
            {' '}{new Date(prospectEndMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}). Overlapping rows highlighted.
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Typography variant="body2" color="textSecondary">
            Loading…
          </Typography>
        )}
        {!loading && rows.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No approved bookings on this date yet.
          </Typography>
        )}
        <List dense>
          {rows.map((r) => {
            const overlap = overlapsProspect(r);
            return (
              <React.Fragment key={r.id}>
                <ListItem
                  style={
                    overlap
                      ? { background: '#ffebee', borderLeft: '4px solid #c62828' }
                      : {}
                  }
                >
                  <ListItemText
                    primary={(() => {
                      const cleanTables =
                        r.tableNames && r.tableNames !== 'null'
                          ? r.tableNames
                          : '';
                      return (
                        <span>
                          <strong>
                            {fmtTime(r.reservedAt)} –{' '}
                            {new Date(endOf(r)).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </strong>
                          {'  '}
                          {cleanTables ? (
                            <Chip
                              size="small"
                              label={`Tables: ${cleanTables}`}
                              style={{
                                marginLeft: 8,
                                background: overlap ? '#c62828' : '#1976d2',
                                color: 'white',
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="No table assigned"
                              style={{ marginLeft: 8 }}
                            />
                          )}
                        </span>
                      );
                    })()}
                    secondary={
                      <span>
                        {r.name} · party of {r.partySize} ·{' '}
                        <em>ref {r.externalReference}</em>
                      </span>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
